import { useState } from 'react';
import { Field, Input, Button, VStack, Box, Icon, Separator, Grid } from '@chakra-ui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import ContentList from './content-list';
import ContentBlock from './content-block';
import { ContentType, Content, SectionForm, GroupForm } from '@/lib/types';

interface Props {
  workspaceId: number;
  index: number;
  item: SectionForm;
  contentTypes: ContentType[];
  contents: Content[];
  onChange(row: SectionForm, index: number): void;
}

export default function GroupBlock({
  workspaceId,
  index,
  item,
  contentTypes,
  contents,
  onChange,
}: Props) {
  const [contentType, setContentType] = useState<ContentType>();
  const [selectedGroup, setSelectedGroup] = useState<GroupForm>();
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>();
  const contentDrawer = useModalToggle();

  function addContent(refs: string[]) {
    if (selectedGroup && selectedGroupIndex !== undefined && selectedGroupIndex >= -1) {
      const updatedGroup = {
        ...selectedGroup,
        contents: refs,
      };
      const updatedSection = { ...item, groups: [...item.groups] };
      updatedSection.groups[selectedGroupIndex] = updatedGroup;
      onChange(updatedSection, index);
    }
  }

  function applyChanges(groupItem: GroupForm, groupIndex: number) {
    const updatedGroups = item.groups.map((group, index) =>
      index === groupIndex ? groupItem : group,
    );
    const updatedSection = { ...item, groups: updatedGroups };
    onChange(updatedSection, index);
  }

  function removeItem(groupIndex: number, referencId: string) {
    if (groupIndex < 0 || groupIndex >= item.groups.length) {
      return;
    }

    const currentGroup = item.groups[groupIndex];
    const updatedContents = currentGroup.contents.filter((id) => id !== referencId);
    const updatedGroup = {
      ...currentGroup,
      contents: updatedContents,
    };

    const updatedGroups = [...item.groups];
    updatedGroups[groupIndex] = updatedGroup;

    const updatedSection = { ...item, groups: updatedGroups };

    onChange(updatedSection, index);
  }

  return (
    <>
      {item.groups.map((group, groupIndex) => (
        <Box key={`group` + groupIndex} w={'full'}>
          <VStack gap={4} align={'stretch'}>
            <Field.Root>
              <Input
                id={`group-name-${index}-${groupIndex}`}
                value={group.name}
                placeholder={'Name this group...'}
                onChange={(e) => {
                  const updatedGroup = item.groups.map((group, subIndex) =>
                    subIndex === groupIndex ? { ...group, name: e.target.value } : group,
                  );
                  onChange({ ...item, groups: updatedGroup }, index);
                }}
              />
            </Field.Root>
            <VStack gap={2}>
              <ContentList
                groupItem={group}
                groupIndex={groupIndex}
                contents={contents}
                onChange={applyChanges}
                removeItem={removeItem}
              />
            </VStack>
            <Grid
              templateColumns={{
                base: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              }}
              gap={4}
            >
              {contentTypes.map((type) => (
                <Button
                  key={type.id}
                  variant={'surface'}
                  size={'sm'}
                  onClick={() => {
                    setContentType(type);
                    setSelectedGroup(group);
                    setSelectedGroupIndex(groupIndex);
                    contentDrawer.open();
                  }}
                >
                  <Icon as={PlusIcon} w={4} h={4} />
                  {type.name}
                </Button>
              ))}
              <Button
                variant={'surface'}
                size={'sm'}
                onClick={() => {
                  const updatedGroups = item.groups.filter(
                    (_, subIndex) => subIndex !== groupIndex,
                  );
                  onChange({ ...item, groups: updatedGroups }, index);
                }}
              >
                <Icon as={TrashIcon} w={3} h={3} />
                Remove Group
              </Button>
            </Grid>
          </VStack>
          <Separator borderColor={'border'} mt={4} mb={0} />
        </Box>
      ))}

      {workspaceId && (
        <ContentBlock
          {...contentDrawer.props}
          workspaceId={workspaceId}
          contentType={contentType}
          contentRefs={selectedGroup?.contents || []}
          onSuccess={addContent}
        />
      )}
    </>
  );
}
